"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Search, Star } from "lucide-react";

export default function WebsiteSeoScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Google ready", "Listo para Google")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("They search. ", "Buscan. ")}
            <span className="text-redy-charcoal/30">{t("Google can read you.", "Google te puede leer.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Schema markup, sitemap, OpenGraph, mobile signals — all generated automatically. Your hours, location and menu are marked up so Google can read them when someone searches \"birria near me.\"",
              "Schema markup, sitemap, OpenGraph, señales mobile — todo generado automático. Tus horarios, ubicación y menú van marcados para que Google los lea cuando alguien busca \"birria cerca de mí.\""
            )}
          </p>
        </motion.div>

        {/* Google search result mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white border border-black/5 rounded-[20px] p-6 md:p-8 max-w-[760px] mx-auto shadow-[0_20px_60px_rgba(0,0,0,0.04)]"
        >
          {/* Google search bar */}
          <div className="flex items-center gap-3 bg-[#FAFAF8] border border-black/5 rounded-full px-4 py-3 mb-6">
            <Search className="w-4 h-4 text-redy-secondary/55" />
            <span className="text-[14px] font-medium text-redy-charcoal/85">
              {t("birria tacos near me", "birria tacos cerca de mí")}
            </span>
          </div>

          {/* Search result — featured */}
          <div className="space-y-1 mb-2">
            <div className="text-[10px] text-redy-secondary">
              pepitos.useredy.com › {t("menu", "menu")}
            </div>
            <div className="text-[18px] font-bold leading-tight" style={{ color: "#1A0DAB" }}>
              Pepitos — Authentic Birria Tacos in Dallas
            </div>
            <div className="text-[13px] text-redy-charcoal/75 leading-relaxed">
              {t(
                "Birria, Al Pastor, Carnitas and Quesabirria from $4.00. Open today 11 AM – 10 PM. Order pickup directly...",
                "Birria, Al Pastor, Carnitas y Quesabirria desde $4.00. Hoy 11 AM – 10 PM. Pide para pickup directo..."
              )}
            </div>
          </div>

          {/* Rich snippet — rating + hours */}
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[12px]">
            <div className="flex items-center gap-1">
              <span className="font-bold text-redy-charcoal">4.8</span>
              <div className="flex">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-2.5 h-2.5 fill-[#FBBC04] stroke-[#FBBC04]" />
                ))}
              </div>
              <span className="text-redy-secondary">(412)</span>
            </div>
            <div className="flex items-center gap-1.5 text-redy-secondary">
              <span className="font-bold" style={{ color: "#2ECC71" }}>{t("Open now", "Abierto")}</span>
              <span>· {t("Closes 10 PM", "Cierra 10 PM")}</span>
            </div>
            <div className="text-redy-secondary">
              <span className="font-bold text-redy-charcoal">$</span> · {t("Mexican · Tacos", "Mexicana · Tacos")}
            </div>
          </div>

          {/* What's indexed */}
          <div className="mt-6 pt-5 border-t border-black/5">
            <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-3">
              {t("What we mark up for Google (auto)", "Lo que marcamos para Google (auto)")}
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                t("Menu items", "Items del menú"),
                t("Prices", "Precios"),
                t("Hours", "Horarios"),
                t("Location", "Ubicación"),
                t("Phone", "Teléfono"),
                t("Ratings (once you have reviews)", "Calificaciones (cuando tengas reviews)"),
                t("Photos", "Fotos"),
              ].map((tag) => (
                <span
                  key={tag}
                  className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                  style={{
                    backgroundColor: "rgba(244,114,43,0.10)",
                    color: "#F4722B",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
